import { useState, useContext, useEffect, useCallback } from 'react'
import { Button, Title } from '../../../../ui'
import Tabs from '../../../../tabs/Tabs'
import GlobalContext from '../../../../../../context/global-context'
import mercadoPagoAuthImage from '../../../../../../assets/integrations/mercado-pago-auth.png'
import logoLuca from '../../../../../../assets/logoLuca.png'
import integrationsAPI from '../../../../../../api/integrations.api'
import businessAPI from '../../../../../../api/business.api'
import { REACT_APP_MERCADOPAGO_REDIRECT_URI } from '../../../../../utils/constant'
import { sileo } from 'sileo'

const generateAccountId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return `mp-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

const MercadoPagoIntegrationContent = ({ isModal = false }) => {
  const { currentBusiness } = useContext(GlobalContext)
  const [activeTab, setActiveTab] = useState(0)
  const [mercadoPagoAccessToken, setMercadoPagoAccessToken] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [accounts, setAccounts] = useState([])
  const [accountsLoading, setAccountsLoading] = useState(false)

  const fetchAccounts = useCallback(async () => {
    if (!currentBusiness?.id) return
    setAccountsLoading(true)
    try {
      const response = await businessAPI.getMercadoPagoAccounts(currentBusiness.id)
      setAccounts(Array.isArray(response.data) ? response.data : [])
    } catch (err) {
      console.error('Error fetching Mercado Pago accounts:', err)
    } finally {
      setAccountsLoading(false)
    }
  }, [currentBusiness?.id])

  useEffect(() => {
    fetchAccounts()
  }, [fetchAccounts])

  const handleTabChange = index => {
    setActiveTab(index)
    setMercadoPagoAccessToken('')
    setSuccess(false)
  }

  const handleConnectWithLink = async () => {
    setLoading(true)

    try {
      const mpAccountId = generateAccountId()
      const response = await integrationsAPI.getMercadoPagoOAuthUrl(currentBusiness.id, {
        redirect_uri: `${REACT_APP_MERCADOPAGO_REDIRECT_URI}?businessId=${currentBusiness.id}&mpAccountId=${mpAccountId}`,
        organization_id: currentBusiness.organizationId
      })

      const redirectUrl = response.data?.authorization_url || response.data?.url

      if (redirectUrl) {
        window.open(redirectUrl, '_blank')
        sileo.info({
          title: 'Ventana Abierta',
          description: 'Se ha abierto una nueva ventana para autorizar Mercado Pago'
        })
      } else {
        sileo.error({
          title: 'Error de Generación',
          description: 'No se pudo generar la URL de autorización 🤯'
        })
      }
    } catch (err) {
      console.error('Error getting Mercado Pago OAuth URL:', err)
      sileo.error({
        title: 'Error de Conexión',
        description: 'Error al obtener la URL de autorización. Intenta nuevamente 🤯'
      })
    } finally {
      setLoading(false)
    }
  }

  const handleConnectWithToken = async () => {
    if (!mercadoPagoAccessToken.trim()) {
      sileo.info({
        title: 'Token Requerido',
        description: 'Por favor ingresa un token de acceso válido ⚠️'
      })
      return
    }

    setLoading(true)

    try {
      const response = await businessAPI.connectMercadoPagoWithAccessToken(
        currentBusiness.id,
        mercadoPagoAccessToken.trim()
      )

      if (response.status === 200 || response.status === 201) {
        setSuccess(true)
        sileo.success({
          title: 'Conexión Exitosa',
          description: 'Mercado Pago ha sido conectado exitosamente 👌'
        })
      } else {
        sileo.error({
          title: 'Error de Token',
          description: 'Error al conectar con el token proporcionado 🤯'
        })
      }
    } catch (err) {
      console.error('Error connecting Mercado Pago with token:', err)
      sileo.error({
        title: 'Fallo de Conexión',
        description:
          err.response?.data?.message ||
          'Error al conectar con Mercado Pago. Verifica el token e intenta nuevamente. 🤯'
      })
    } finally {
      setLoading(false)
    }
  }

  const resetState = () => {
    setMercadoPagoAccessToken('')
    setSuccess(false)
    setLoading(false)
  }

  return (
    <div className='relative w-full mx-auto'>
      <Title className='text-2xl mb-6'>¿Cómo conectar Mercado Pago a Luca?</Title>

      {!accountsLoading && accounts.length > 0 && (
        <div className='bg-gray-50 rounded-lg p-4 border border-gray-200 mb-6'>
          <h3 className='text-sm font-semibold text-gray-700 mb-3'>
            Cuentas de Mercado Pago conectadas ({accounts.length})
          </h3>
          <ul className='space-y-2'>
            {accounts.map(account => (
              <li
                key={account.id}
                className='flex items-center justify-between px-3 py-2 rounded-md border bg-white border-gray-200'
              >
                <div>
                  <p className='text-sm font-medium text-gray-800'>
                    {account.mpEmail || account.numeroCuenta}
                  </p>
                  <p className='text-xs text-gray-500'>Cuenta #{account.numeroCuenta}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {success ? (
        <div className='flex flex-col items-center justify-center py-12 space-y-6'>
          <div className='text-center'>
            <div className='text-3xl font-bold text-primary mb-4'>
              ¡Mercado Pago ha sido integrado correctamente!
            </div>
          </div>
          <div className='flex justify-center mt-8'>
            <img src={logoLuca} alt='Logo Luca' className='h-24 w-auto' />
          </div>
          {!isModal && (
            <div className='flex justify-end w-full mt-6'>
              <Button
                onClick={resetState}
                className='text-sm bg-accent2 text-white rounded-full'
                title='Cerrar'
              />
            </div>
          )}
        </div>
      ) : (
        <div className='buk-modal-tabs relative'>
          <Tabs
            tabs={[
              {
                title: 'Autorizar con enlace (OAuth)',
                content: (
                  <div className='space-y-6'>
                    <div className='bg-gray-50 rounded-lg p-6 border border-gray-200'>
                      <div className='space-y-4 text-gray-700'>
                        <div className='flex items-start gap-3'>
                          <span className='text-xl font-bold text-primary'>1.</span>
                          <div>
                            <p className='mb-2'>
                              Haz clic en el botón &ldquo;Autorizar con Mercado Pago&rdquo; para
                              abrir una nueva ventana donde iniciarás sesión en tu cuenta de Mercado
                              Pago.
                            </p>
                          </div>
                        </div>
                        <div className='flex items-start gap-3'>
                          <span className='text-xl font-bold text-primary'>2.</span>
                          <div>
                            <p className='mb-2'>
                              Selecciona tu país y luego inicia sesión en tu cuenta de Mercado Pago.
                            </p>
                          </div>
                        </div>
                        <div className='flex items-start gap-3'>
                          <span className='text-xl font-bold text-primary'>3.</span>
                          <div>
                            <p className='mb-2'>
                              Autoriza el acceso a Luca y serás redirigido automáticamente de vuelta
                              a la aplicación.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className='mt-6 flex justify-center'>
                      <img
                        src={mercadoPagoAuthImage}
                        alt='Mercado Pago Auth'
                        className='max-w-full h-auto rounded-lg shadow-md'
                      />
                    </div>

                    {/* {error && (
                      <div className='bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg'>
                        {error}
                      </div>
                    )} */}

                    <div className='flex justify-end w-full mt-6'>
                      <Button
                        onClick={handleConnectWithLink}
                        disabled={loading}
                        className='text-sm bg-accent2 text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed'
                        title={
                          loading
                            ? 'Generando enlace...'
                            : accounts.length > 0
                              ? 'Conectar otra cuenta de Mercado Pago'
                              : 'Autorizar con Mercado Pago'
                        }
                      />
                    </div>
                  </div>
                )
              },
              {
                title: 'Token de acceso',
                content: (
                  <div className='space-y-6'>
                    <div className='bg-gray-50 rounded-lg p-6 border border-gray-200'>
                      <h3 className='text-lg font-semibold mb-4'>Conectar con Token de Acceso</h3>
                      <div className='space-y-4 text-gray-700'>
                        <div className='flex items-start gap-3'>
                          <span className='text-xl font-bold text-primary'>1.</span>
                          <div>
                            <p className='mb-2'>
                              Si ya cuentas con un <strong>Access Token</strong> de Mercado Pago,
                              puedes copiarlo y pegarlo directamente aquí.
                            </p>
                          </div>
                        </div>
                        <div className='flex items-start gap-3'>
                          <span className='text-xl font-bold text-primary'>2.</span>
                          <div>
                            <p className='mb-2'>
                              Pega tu token en el campo de texto y haz clic en
                              &ldquo;Conectar&rdquo; para vincular tu cuenta.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className='bg-white p-4 border border-gray-200 rounded-lg'>
                      <label className='block text-sm font-semibold text-gray-700 mb-2'>
                        Access Token de Mercado Pago:
                      </label>
                      <textarea
                        onChange={e => setMercadoPagoAccessToken(e.target.value)}
                        placeholder='Pega aquí tu access token de Mercado Pago...'
                        className='w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none'
                        rows={3}
                        disabled={loading}
                      />
                    </div>

                    {/* {error && (
                      <div className='bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg'>
                        {error}
                      </div>
                    )} */}

                    <div className='flex justify-end w-full mt-6'>
                      <Button
                        onClick={handleConnectWithToken}
                        disabled={loading || !mercadoPagoAccessToken.trim()}
                        className='text-sm bg-accent2 text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed'
                        title={loading ? 'Conectando...' : 'Conectar con Token'}
                      />
                    </div>
                  </div>
                )
              }
            ]}
            activeIndex={activeTab}
            onTabClick={handleTabChange}
          />
        </div>
      )}
    </div>
  )
}

export default MercadoPagoIntegrationContent
